import { useNavigate } from '@tanstack/react-router'
import { useCallback } from 'react'
import { useBasePath } from '../../../hooks/use-base-path'
import { openIntercom } from '../../../lib/intercom'
import { type HomeCtaTarget, outreachTabFor } from '../config/cta-routes'

export interface HomeCtaHandlers {
  /** Opens the LinkedIn connect dialog, mounted by the caller. */
  /** Opens the top-up dialog, mounted by the caller. */
  onAddFunds: () => void
  onResumeSending: () => void
  onPauseSending: () => void
  onApproveAll: () => void
  onApproveContacts: () => void
  onUploadDeck: () => void
  onCopyShareLink: () => void
  onEscapeToShare: () => void
}

/**
 * Turn a CTA target into something callable.
 *
 * Dialogs and mutations stay with the caller: the wizard mounts them, this only
 * decides which one a given state's button reaches. Keeping the decision in one
 * table and the wiring in one hook is what stops the routing being restated per
 * card.
 */
export function useHomeCtaRunner(
  handlers: HomeCtaHandlers,
  /** What the campaign-board routes need to pick a tab — see outreachTabFor. */
  campaign?: { investorTrackEnabled?: boolean; tracks?: readonly ('investor' | 'warm_intro')[] }
) {
  const navigate = useNavigate()
  const basePath = useBasePath()

  return useCallback(
    (target: HomeCtaTarget | undefined) => {
      if (!target) return

      if (target.kind === 'route') {
        // Every route to the campaign board picks its tab here rather than in
        // the table. The board opens the investor campaign by default, so a
        // founder running warm intros only was sent to an empty board — the
        // same wrong landing whether the card said review, launch, connect or
        // approve. `/outreach/replies` is not the board and keeps its own path.
        const search =
          target.to === '/outreach'
            ? { ...target.search, tab: outreachTabFor(campaign) }
            : target.search
        navigate({
          to: `${basePath}${target.to}`,
          search: search as never,
        })
        return
      }

      switch (target.action) {
        case 'upload-deck':
          handlers.onUploadDeck()
          return
        case 'add-funds':
          handlers.onAddFunds()
          return
        case 'resume-sending':
          handlers.onResumeSending()
          return
        case 'pause-sending':
          handlers.onPauseSending()
          return
        case 'approve-all':
          handlers.onApproveAll()
          return
        case 'approve-contacts':
          handlers.onApproveContacts()
          return
        case 'support':
          openIntercom()
          return
        case 'copy-share-link':
          handlers.onCopyShareLink()
          return
        case 'escape-to-share':
          handlers.onEscapeToShare()
          return
      }
    },
    [navigate, basePath, handlers, campaign]
  )
}
